import React, { useEffect, useMemo, useState } from 'react';
import {
  Package,
  Search,
  Filter,
  RefreshCw,
  Truck,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Eye,
  MapPin,
  User,
  ShoppingBag,
  Navigation,
  Phone,
  X
} from 'lucide-react';
import { apiClient, endpoints } from '../../api';
import { getApiKey } from '../../utils/auth';
import './AdminPackages.css';

const PACKAGE_PAGE_SIZE = 100;

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `Rs ${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const normalized = String(value).replace(/_/g, ' ').toLowerCase();
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeStatusText = (value) => normalizeText(value).replace(/[-\s]+/g, '_');

const firstValue = (...values) => (
  values.find((value) => {
    if (value === undefined || value === null) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized !== '' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '-';
  })
);

const parseNumber = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return 0;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const parseDistanceKm = (delivery) => {
  const explicitKm = parseNumber(
    delivery?.distance_km,
    delivery?.distanceInKm,
    delivery?.distance_in_km
  );
  if (explicitKm > 0) return explicitKm;

  const meters = parseNumber(
    delivery?.distance_meters,
    delivery?.distanceInMeters,
    delivery?.distance_meter
  );
  if (meters > 0) return meters / 1000;

  const rawDistance = parseNumber(delivery?.distance, delivery?.trip_distance);
  return rawDistance > 100 ? rawDistance / 1000 : rawDistance;
};

const formatDistanceKm = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'N/A';
  const formatted = numeric >= 10 ? numeric.toFixed(0) : numeric.toFixed(1);
  return `${formatted.replace(/\.0$/, '')} km`;
};

const hasAssignedPartner = (delivery) => {
  const partnerIdentity = firstValue(
    delivery?.delivery_partner_id,
    delivery?.partner_id,
    delivery?.deliveryPartnerId,
    delivery?.partnerId,
    delivery?.partner_code,
    delivery?.delivery_partner_code
  );
  const partnerName = normalizeText(firstValue(
    delivery?.partner_name,
    delivery?.delivery_partner_name,
    delivery?.full_name
  ));

  return Boolean(partnerIdentity) || (partnerName && !['unassigned', 'unassigned partner', 'n/a'].includes(partnerName));
};

const resolvePackageStatus = (delivery) => {
  const status = normalizeStatusText(
    firstValue(
      delivery?.delivery_status,
      delivery?.deliveryStatus,
      delivery?.package_status,
      delivery?.packageStatus,
      delivery?.status,
      delivery?.shipment_status
    )
  );
  const compact = status.replace(/_/g, '');
  const hasPartner = hasAssignedPartner(delivery);
  const hasAcceptedTime = Boolean(firstValue(delivery?.accepted_at, delivery?.acceptedAt));
  const hasPickedUpTime = Boolean(firstValue(
    delivery?.picked_up_at,
    delivery?.pickedUpAt,
    delivery?.picked_at,
    delivery?.pickedAt,
    delivery?.pickup_at,
    delivery?.pickupAt
  ));
  const hasStartedDeliveryTime = Boolean(firstValue(
    delivery?.started_at,
    delivery?.startedAt,
    delivery?.delivery_started_at,
    delivery?.deliveryStartedAt,
    delivery?.start_delivery_at,
    delivery?.startDeliveryAt,
    delivery?.in_transit_at,
    delivery?.inTransitAt
  ));
  const hasDeliveredTime = Boolean(firstValue(
    delivery?.delivered_at,
    delivery?.deliveredAt,
    delivery?.completed_at,
    delivery?.completedAt
  ));
  const currentDropIndex = parseNumber(
    delivery?.current_drop_index,
    delivery?.currentDropIndex
  );
  const totalDrops = parseNumber(
    delivery?.total_drops,
    delivery?.totalDrops
  );
  const dropStatuses = [
    ...parseJsonArray(delivery?.drop_statuses),
    ...parseJsonArray(delivery?.dropStatuses)
  ]
    .map((item) => normalizeStatusText(item))
    .filter(Boolean);
  const hasDeliveredDrop = dropStatuses.some((item) => ['delivered', 'completed'].includes(item));
  const hasTransitDrop = dropStatuses.some((item) => ['in_transit', 'on_the_way'].includes(item));
  const hasPickedDrop = dropStatuses.some((item) => ['picked_up', 'picked', 'ready_for_delivery'].includes(item));
  const isFullyDeliveredByDrops = totalDrops > 0 && currentDropIndex >= totalDrops;

  if (hasDeliveredTime || hasDeliveredDrop || isFullyDeliveredByDrops) return 'delivered';
  if (['delivered', 'complete', 'completed'].includes(compact)) return 'delivered';
  if (hasStartedDeliveryTime || hasTransitDrop) return 'on_the_way';
  if (['intransit', 'ontheway', 'outfordelivery', 'readyfordelivery', 'onthewaytodelivery'].includes(compact)) return 'on_the_way';
  if (hasPickedUpTime || hasPickedDrop) return 'picked_up';
  if (['picked', 'pickup', 'pickedup', 'packagepickedup'].includes(compact)) return 'picked_up';
  if (['assigned', 'accepted', 'confirmed', 'partnerassigned', 'deliverypartnerassigned', 'assignedtopartner', 'riderassigned', 'driverassigned'].includes(compact)) return 'assigned';
  if (['cancelled', 'canceled', 'cancel', 'rejected', 'returned', 'failed'].includes(status)) return 'issue';
  if (['pending', 'placed', 'available', 'booked', 'processing'].includes(status)) {
    return hasPartner || hasAcceptedTime ? 'assigned' : 'booked';
  }

  return hasPartner || hasAcceptedTime ? 'assigned' : 'booked';
};

const getStatusMeta = (status) => {
  const statusMap = {
    booked: {
      label: 'Package Booked',
      className: 'status-booked',
      icon: Clock
    },
    assigned: {
      label: 'Partner Assigned',
      className: 'status-assigned',
      icon: ShoppingBag
    },
    picked_up: {
      label: 'Picked Up',
      className: 'status-picked-up',
      icon: Package
    },
    on_the_way: {
      label: 'Package On The Way',
      className: 'status-on-the-way',
      icon: Truck
    },
    in_transit: {
      label: 'Package On The Way',
      className: 'status-on-the-way',
      icon: Truck
    },
    accepted: {
      label: 'Partner Assigned',
      className: 'status-assigned',
      icon: ShoppingBag
    },
    delivered: {
      label: 'Delivered',
      className: 'status-delivered',
      icon: CheckCircle2
    },
    issue: {
      label: 'Issue',
      className: 'status-issue',
      icon: AlertTriangle
    }
  };

  return statusMap[status] || statusMap.booked;
};

const extractPackageList = (payload) => {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.orders,
    payload?.packages,
    payload?.packageOrders,
    payload?.package_orders,
    payload?.items,
    payload?.results,
    payload?.data,
    payload?.data?.orders,
    payload?.data?.packages,
    payload?.data?.packageOrders,
    payload?.data?.package_orders,
    payload?.data?.items,
    payload?.data?.results
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

const extractPagination = (payload) => (
  payload?.pagination ||
  payload?.data?.pagination ||
  payload?.meta ||
  payload?.data?.meta ||
  {}
);

const readTotalPages = (payload) => {
  const pagination = extractPagination(payload);
  return Math.max(
    1,
    parseNumber(
      pagination.total_pages,
      pagination.totalPages,
      pagination.pages,
      pagination.last_page,
      pagination.lastPage,
      1
    )
  );
};

const normalizePackage = (delivery) => {
  const packageStatus = resolvePackageStatus(delivery);
  const distanceKm = parseDistanceKm(delivery);
  const estimatedMinutes = parseNumber(
    delivery?.estimated_time_minutes,
    delivery?.estimated_minutes,
    delivery?.eta_minutes,
    delivery?.estimated_time,
    delivery?.estimatedTime
  );

  return {
    id: firstValue(delivery?.delivery_order_id, delivery?.id, delivery?.order_id),
    deliveryOrderId: firstValue(delivery?.delivery_order_id, delivery?.id, delivery?.order_id),
    orderNumber: firstValue(delivery?.delivery_code, delivery?.order_number, delivery?.tracking_code, delivery?.id) || 'N/A',
    packageId: firstValue(delivery?.package_id, delivery?.delivery_code, delivery?.tracking_code, delivery?.order_number, `PKG-${delivery?.id || 'NA'}`),
    packageType: firstValue(delivery?.package_type, delivery?.parcel_type, delivery?.item_type, delivery?.packageType, 'Package'),
    customerName: firstValue(delivery?.customer_name, delivery?.full_name, delivery?.receiver_name, 'Unknown Customer'),
    customerPhone: firstValue(delivery?.customer_phone, delivery?.phone, delivery?.receiver_phone, 'N/A'),
    deliveryPartnerName: firstValue(delivery?.partner_name, delivery?.delivery_partner_name, delivery?.full_name, 'Unassigned Partner'),
    deliveryPartnerPhone: firstValue(delivery?.partner_phone, delivery?.delivery_partner_phone, delivery?.phone, 'N/A'),
    pickupAddress: firstValue(delivery?.pickup_address, delivery?.pickup_location, delivery?.source_address, delivery?.pickupAddress, 'Pickup address not available'),
    dropAddress: firstValue(delivery?.delivery_address, delivery?.drop_address, delivery?.dropoff_address, delivery?.destination_address, delivery?.dropAddress, 'Drop address not available'),
    paymentStatus: firstValue(delivery?.payment_status, 'pending'),
    paymentType: firstValue(delivery?.payment_type, delivery?.payment_mode, 'N/A'),
    totalAmount: parseNumber(delivery?.total_amount, delivery?.amount, delivery?.order_amount, delivery?.delivery_charge),
    packageCount: Math.max(1, parseNumber(delivery?.package_count, delivery?.packages_count, 1)),
    quantity: Math.max(1, parseNumber(delivery?.total_quantity, delivery?.item_count, delivery?.quantity, 1)),
    itemCount: Math.max(1, parseNumber(delivery?.item_count, delivery?.quantity, 1)),
    packageStatus,
    rawStatus: firstValue(delivery?.delivery_status, delivery?.status, ''),
    createdAt: firstValue(delivery?.created_at, delivery?.booked_at, delivery?.accepted_at),
    updatedAt: firstValue(delivery?.updated_at, delivery?.last_location_update, delivery?.accepted_at, delivery?.created_at),
    acceptedAt: firstValue(delivery?.accepted_at, delivery?.updated_at),
    estimatedDispatch: firstValue(delivery?.accepted_at, delivery?.estimated_pickup_time, delivery?.created_at),
    trackingCode: firstValue(delivery?.tracking_code, delivery?.delivery_code, `TRK-${delivery?.id || 'NA'}`),
    notes: firstValue(delivery?.notes, delivery?.special_instructions, delivery?.instructions, ''),
    distanceKm,
    estimatedMinutes,
    deliveryCharge: parseNumber(delivery?.delivery_charge, delivery?.delivery_fee, delivery?.total_amount, delivery?.amount),
    partnerCode: firstValue(delivery?.partner_code, delivery?.delivery_partner_code, 'N/A'),
    currentAddress: firstValue(delivery?.current_address, delivery?.pickup_address, delivery?.delivery_address, 'N/A')
  };
};

const matchesSearch = (pkg, searchValue) => {
  const lookup = normalizeText(searchValue);
  if (!lookup) return true;

  return [
    pkg.packageId,
    pkg.orderNumber,
    pkg.packageType,
    pkg.customerName,
    pkg.customerPhone,
    pkg.deliveryPartnerName,
    pkg.pickupAddress,
    pkg.dropAddress,
    pkg.trackingCode
  ].some((value) => normalizeText(value).includes(lookup));
};

const AdminPackages = () => {
  const [allPackages, setAllPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchPackages = async ({ background = false } = {}) => {
    try {
      if (!background) {
        setLoading(true);
      }
      const apiKey = getApiKey();
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token') || '';
      const headers = {
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        ...(apiKey ? { 'x-admin-key': apiKey } : {})
      };

      const fetchPage = (page) => apiClient.get(endpoints.packageOrders, {
        headers,
        params: {
          page,
          limit: PACKAGE_PAGE_SIZE,
          per_page: PACKAGE_PAGE_SIZE
        }
      });

      const firstResponse = await fetchPage(1);
      const firstData = firstResponse.data;
      const totalPages = readTotalPages(firstData);
      const list = [...extractPackageList(firstData)];

      if (totalPages > 1) {
        const remainingResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) => fetchPage(index + 2))
        );
        remainingResponses.forEach((response) => {
          list.push(...extractPackageList(response.data));
        });
      }

      const normalizedPackages = list.map(normalizePackage);

      setAllPackages(normalizedPackages);
      setSelectedPackage((current) => {
        if (!current?.id) return current;
        return normalizedPackages.find((pkg) => String(pkg.id) === String(current.id)) || current;
      });
      if (!background) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (!background) {
        setAllPackages([]);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  };

  const handleViewPackage = async (pkg) => {
    setSelectedPackage(pkg);

    if (!pkg?.id) {
      return;
    }

    try {
      setLoadingDetails(true);
      const apiKey = getApiKey();
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token') || '';
      const headers = {
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        ...(apiKey ? { 'x-admin-key': apiKey } : {})
      };

      const { data } = await apiClient.get(endpoints.packageOrderById(pkg.id), { headers });
      const detailRoot =
        data?.order ||
        data?.package ||
        data?.data?.order ||
        data?.data?.package ||
        data?.data ||
        data;

      if (detailRoot && typeof detailRoot === 'object') {
        setSelectedPackage(normalizePackage(detailRoot));
      }
    } catch (error) {
      console.error('Error fetching package details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      fetchPackages({ background: true });
    }, 12000);

    return () => window.clearInterval(interval);
  }, []);

  const filteredPackages = useMemo(() => {
    return allPackages.filter((pkg) => {
      if (!matchesSearch(pkg, searchQuery)) return false;
      if (statusFilter !== 'all' && pkg.packageStatus !== statusFilter) return false;
      if (paymentFilter !== 'all' && normalizeText(pkg.paymentStatus) !== normalizeText(paymentFilter)) return false;

      const createdAt = pkg.createdAt ? new Date(pkg.createdAt) : null;
      if (dateFrom && createdAt && createdAt < new Date(`${dateFrom}T00:00:00`)) return false;
      if (dateTo && createdAt && createdAt > new Date(`${dateTo}T23:59:59`)) return false;

      return true;
    });
  }, [allPackages, searchQuery, statusFilter, paymentFilter, dateFrom, dateTo]);

  const totalPackages = filteredPackages.length;
  const totalPages = totalPackages > 0 ? Math.ceil(totalPackages / rowsPerPage) : 0;
  const safeCurrentPage = totalPages === 0 ? 1 : Math.min(currentPage, totalPages);
  const paginatedPackages = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * rowsPerPage;
    return filteredPackages.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredPackages, rowsPerPage, safeCurrentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, paymentFilter, dateFrom, dateTo, rowsPerPage]);

  const packageStats = useMemo(() => {
    return allPackages.reduce((acc, pkg) => {
      acc.total += 1;
      if (Object.prototype.hasOwnProperty.call(acc, pkg.packageStatus)) {
        acc[pkg.packageStatus] += 1;
      } else {
        acc.issue += 1;
      }
      return acc;
    }, {
      total: 0,
      booked: 0,
      assigned: 0,
      picked_up: 0,
      on_the_way: 0,
      delivered: 0,
      issue: 0
    });
  }, [allPackages]);

  const statCards = [
    { key: 'total', label: 'Total Packages', value: packageStats.total, icon: Package },
    { key: 'booked', label: 'Booked', value: packageStats.booked, icon: Clock },
    { key: 'assigned', label: 'Partner Assigned', value: packageStats.assigned, icon: ShoppingBag },
    { key: 'picked_up', label: 'Picked Up', value: packageStats.picked_up, icon: Package },
    { key: 'on_the_way', label: 'On The Way', value: packageStats.on_the_way, icon: Truck },
    { key: 'delivered', label: 'Delivered', value: packageStats.delivered, icon: CheckCircle2 }
  ];

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'booked', label: 'Booked' },
    { id: 'assigned', label: 'Partner Assigned' },
    { id: 'picked_up', label: 'Picked Up' },
    { id: 'on_the_way', label: 'On The Way' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'issue', label: 'Issue' }
  ];

  return (
    <div className="admin-packages">
      <div className="admin-packages-header">
        <div className="header-copy">
          <h1>Packages Management</h1>
        </div>
        <button className="btn btn-secondary" onClick={fetchPackages}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      <div className="packages-stats-grid">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.key} className="package-stat-card">
              <div className="package-stat-icon">
                <Icon size={20} />
              </div>
              <div>
                <span className="package-stat-label">{card.label}</span>
                <strong className="package-stat-value">{card.value}</strong>
              </div>
            </div>
          );
        })}
      </div>

      <div className="packages-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab ${statusFilter === tab.id ? 'active' : ''}`}
            onClick={() => setStatusFilter(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="packages-filters">
        <div className="filters-primary">
          <div className="search-container">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search package, customer, pickup, drop..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <Filter size={18} />
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Payments</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="payment_received">Received</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <div className="filters-secondary">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="date-input"
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="date-input"
            aria-label="To date"
          />
          <div className="results-count">
            {paginatedPackages.length} package{paginatedPackages.length === 1 ? '' : 's'} on this page
          </div>
        </div>
      </div>

      <div className="packages-shell">
        {loading ? (
          <div className="loading">Loading packages...</div>
        ) : paginatedPackages.length === 0 ? (
          <div className="empty-state">
            <Package size={42} />
            <h3>No packages found</h3>
            <p>Package API se abhi koi record nahi aa raha.</p>
          </div>
        ) : (
          <>
            <div className="packages-table-container">
              <table className="packages-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Customer</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th>Partner</th>
                    <th>Distance / ETA</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPackages.map((pkg) => {
                    const statusMeta = getStatusMeta(pkg.packageStatus);
                    const StatusIcon = statusMeta.icon;

                    return (
                      <tr key={pkg.packageId}>
                        <td>
                          <div className="package-id-cell">
                            <strong>{pkg.packageId}</strong>
                            <span>{pkg.packageType}</span>
                            <span>{pkg.trackingCode}</span>
                          </div>
                        </td>
                        <td>
                          <div className="person-cell">
                            <strong>{pkg.customerName}</strong>
                            <span>{pkg.customerPhone}</span>
                          </div>
                        </td>
                        <td>
                          <div className="person-cell">
                            <strong>{pkg.pickupAddress}</strong>
                            <span>{pkg.dropAddress}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-pill ${statusMeta.className}`}>
                            <StatusIcon size={14} />
                            {statusMeta.label}
                          </span>
                        </td>
                        <td>
                          <div className="person-cell">
                            <strong>{pkg.deliveryPartnerName}</strong>
                            <span>{pkg.partnerCode}</span>
                          </div>
                        </td>
                        <td>
                          <div className="metric-cell">
                            <strong>{formatDistanceKm(pkg.distanceKm)}</strong>
                            <span>{pkg.estimatedMinutes > 0 ? `${pkg.estimatedMinutes} min` : 'ETA pending'}</span>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-info btn-sm"
                            onClick={() => handleViewPackage(pkg)}
                          >
                            <Eye size={14} />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="packages-mobile-list">
              {paginatedPackages.map((pkg) => {
                const statusMeta = getStatusMeta(pkg.packageStatus);
                const StatusIcon = statusMeta.icon;

                return (
                  <article key={`mobile-${pkg.packageId}`} className="package-mobile-card">
                    <div className="package-mobile-top">
                      <div>
                        <h3>{pkg.packageId}</h3>
                        <p>{pkg.packageType}</p>
                      </div>
                      <span className={`status-pill ${statusMeta.className}`}>
                        <StatusIcon size={14} />
                        {statusMeta.label}
                      </span>
                    </div>

                    <div className="package-mobile-grid">
                      <div className="mobile-meta">
                        <User size={14} />
                        <div>
                          <span>Customer</span>
                          <strong>{pkg.customerName}</strong>
                        </div>
                      </div>
                      <div className="mobile-meta">
                        <Truck size={14} />
                        <div>
                          <span>Partner</span>
                          <strong>{pkg.deliveryPartnerName}</strong>
                        </div>
                      </div>
                      <div className="mobile-meta">
                        <MapPin size={14} />
                        <div>
                          <span>Pickup</span>
                          <strong>{pkg.pickupAddress}</strong>
                        </div>
                      </div>
                      <div className="mobile-meta">
                        <Navigation size={14} />
                        <div>
                          <span>Drop</span>
                          <strong>{pkg.dropAddress}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="package-mobile-footer">
                      <span>
                        {pkg.distanceKm > 0
                          ? `${formatDistanceKm(pkg.distanceKm)} / ${pkg.estimatedMinutes > 0 ? `${pkg.estimatedMinutes} min` : 'ETA pending'}`
                          : formatCurrency(pkg.totalAmount)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={() => handleViewPackage(pkg)}
                      >
                        <Eye size={14} />
                        Details
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="pagination-container">
              <div className="pagination-summary">
                Showing {paginatedPackages.length} of {totalPackages} packages (page {safeCurrentPage} of {Math.max(totalPages, 1)})
              </div>
              <div className="pagination-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={safeCurrentPage <= 1}
                >
                  Prev
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.max(totalPages, 1)))}
                  disabled={safeCurrentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div className="pagination-size">
                <span>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10) || 10)}
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedPackage && (
        <div className="modal-overlay">
          <div className="package-details-modal">
            <div className="modal-header">
              <div>
                <h2>Package Details</h2>
                <p>{selectedPackage.packageId}</p>
              </div>
              <button type="button" className="close-btn" onClick={() => setSelectedPackage(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="package-details-body">
              <div className="details-section">
                <h3>Shipment Snapshot</h3>
                <div className="details-grid">
                  <div className="detail-card">
                    <span>Booking Code</span>
                    <strong>{selectedPackage.orderNumber}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Tracking Code</span>
                    <strong>{selectedPackage.trackingCode}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Package Type</span>
                    <strong>{formatTitleCase(selectedPackage.packageType)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Status</span>
                    <strong>{getStatusMeta(selectedPackage.packageStatus).label}</strong>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>People & Route</h3>
                <div className="details-grid">
                  <div className="detail-card">
                    <span>Customer</span>
                    <strong>{selectedPackage.customerName}</strong>
                    <small>{selectedPackage.customerPhone}</small>
                  </div>
                  <div className="detail-card">
                    <span>Delivery Partner</span>
                    <strong>{selectedPackage.deliveryPartnerName}</strong>
                    <small>{selectedPackage.deliveryPartnerPhone}</small>
                  </div>
                  <div className="detail-card full-width">
                    <span>Pickup Address</span>
                    <strong className="address-value">{selectedPackage.pickupAddress}</strong>
                  </div>
                  <div className="detail-card full-width">
                    <span>Drop Address</span>
                    <strong className="address-value">{selectedPackage.dropAddress}</strong>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Delivery Metrics</h3>
                <div className="details-grid">
                  <div className="detail-card">
                    <span>Distance</span>
                    <strong>{formatDistanceKm(selectedPackage.distanceKm)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Estimated Time</span>
                    <strong>{selectedPackage.estimatedMinutes > 0 ? `${selectedPackage.estimatedMinutes} min` : 'N/A'}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Delivery Charge</span>
                    <strong>{formatCurrency(selectedPackage.deliveryCharge)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Payment</span>
                    <strong>{formatTitleCase(selectedPackage.paymentStatus)}</strong>
                  </div>
                  <div className="detail-card">
                    <span>Accepted At</span>
                    <strong>{formatDateTime(selectedPackage.acceptedAt)}</strong>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Operational Notes</h3>
                <div className="notes-panel">
                  {loadingDetails && (
                    <div className="notes-line">
                      <Clock size={16} />
                      <span>Refreshing package details...</span>
                    </div>
                  )}
                  <div className="notes-line">
                    <MapPin size={16} />
                    <span>{selectedPackage.currentAddress}</span>
                  </div>
                  <div className="notes-line">
                    <Phone size={16} />
                    <span>{selectedPackage.deliveryPartnerName} ({selectedPackage.deliveryPartnerPhone})</span>
                  </div>
                  <div className="notes-line">
                    <Clock size={16} />
                    <span>Created {formatDateTime(selectedPackage.createdAt)} / Updated {formatDateTime(selectedPackage.updatedAt)}</span>
                  </div>
                  <div className="notes-line">
                    <AlertTriangle size={16} />
                    <span>{selectedPackage.notes || 'No extra package notes available for this shipment.'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
